import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

interface PageLoaderContextType {
  isLoading: boolean;
  message: string;
  setPageLoading: (loading: boolean, message?: string) => void;
}

const PageLoaderContext = createContext<PageLoaderContextType>({
  isLoading: false,
  message: 'Loading…',
  setPageLoading: () => {},
});

export const usePageLoader = () => useContext(PageLoaderContext);

/**
 * Hook to tie page data loading to the global page loader
 */
export const usePageLoading = (loading: boolean, message?: string) => {
  const { setPageLoading } = usePageLoader();

  useEffect(() => {
    setPageLoading(loading, message);
    return () => {
      setPageLoading(false);
    };
  }, [loading, message, setPageLoading]);
};

// Map routes to intuitive messages
const getRouteTitle = (pathname: string): string => {
  if (pathname.includes('/dashboard')) return 'Loading Dashboard…';
  if (pathname.includes('/invoices/create')) return 'Preparing Invoice…';
  if (pathname.includes('/invoices') && pathname.includes('/edit')) return 'Loading Invoice for Editing…';
  if (pathname.includes('/invoices') && pathname.includes('/preview')) return 'Preparing Invoice Preview…';
  if (pathname.match(/\/invoices\/[^/]+/)) return 'Loading Invoice Details…';
  if (pathname.includes('/invoices')) return 'Loading Invoices…';
  if (pathname.includes('/customers/create')) return 'Preparing Customer…';
  if (pathname.includes('/customers') && pathname.includes('/edit')) return 'Loading Customer for Editing…';
  if (pathname.match(/\/customers\/[^/]+/)) return 'Loading Customer Details…';
  if (pathname.includes('/customers')) return 'Loading Customers…';
  if (pathname.includes('/products/create')) return 'Preparing Product…';
  if (pathname.includes('/products') && pathname.includes('/edit')) return 'Loading Product for Editing…';
  if (pathname.match(/\/products\/[^/]+/)) return 'Loading Product Details…';
  if (pathname.includes('/products')) return 'Loading Products…';
  if (pathname.includes('/payments')) return 'Loading Payments…';
  if (pathname.includes('/reports/sales')) return 'Generating Sales Report…';
  if (pathname.includes('/reports/gst')) return 'Generating GST Report…';
  if (pathname.includes('/reports/customers')) return 'Generating Customer Report…';
  if (pathname.includes('/reports/products')) return 'Generating Product Report…';
  if (pathname.includes('/reports/payments')) return 'Generating Payment Report…';
  if (pathname.includes('/reports')) return 'Loading Reports…';
  if (pathname.includes('/users/create')) return 'Preparing User Form…';
  if (pathname.includes('/users')) return 'Loading Team Members…';
  if (pathname.includes('/roles')) return 'Loading Roles…';
  if (pathname.includes('/audit-logs')) return 'Loading Audit Logs…';
  if (pathname.includes('/settings/company')) return 'Loading Company Settings…';
  if (pathname.includes('/settings/invoice')) return 'Loading Invoice Settings…';
  if (pathname.includes('/settings/tax')) return 'Loading Tax Settings…';
  if (pathname.includes('/settings/templates')) return 'Loading Invoice Templates…';
  if (pathname.includes('/settings/units')) return 'Loading Unit Settings…';
  if (pathname.includes('/settings/profile')) return 'Loading Profile…';
  if (pathname.includes('/settings')) return 'Loading Settings…';
  return 'Loading…';
};

const MIN_SWITCH_TIME = 280; // ms minimum to provide smooth visible transition

export const PageLoaderProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [isRouteSwitching, setIsRouteSwitching] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [customMessage, setCustomMessage] = useState<string | null>(null);

  const prevPathRef = useRef<string>(location.pathname);
  const switchTimerRef = useRef<any>(null);
  const safetyTimerRef = useRef<any>(null);

  // When pathname changes, trigger page switch transition
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname;
      setIsRouteSwitching(true);
      setCustomMessage(null);

      if (switchTimerRef.current) clearTimeout(switchTimerRef.current);
      switchTimerRef.current = setTimeout(() => {
        setIsRouteSwitching(false);
      }, MIN_SWITCH_TIME);
    }
  }, [location.pathname]);

  // Safety timer to prevent loader from hanging indefinitely
  useEffect(() => {
    if (isRouteSwitching || isDataLoading) {
      if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
      safetyTimerRef.current = setTimeout(() => {
        setIsRouteSwitching(false);
        setIsDataLoading(false);
      }, 9000);
    } else {
      if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
    }

    return () => {
      if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
    };
  }, [isRouteSwitching, isDataLoading]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (switchTimerRef.current) clearTimeout(switchTimerRef.current);
      if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
    };
  }, []);

  const setPageLoading = useCallback((loading: boolean, message?: string) => {
    setIsDataLoading(loading);
    if (loading && message) {
      setCustomMessage(message);
    } else if (!loading) {
      setCustomMessage(null);
    }
  }, []);

  const isLoading = isRouteSwitching || isDataLoading;
  const activeMessage = customMessage || getRouteTitle(location.pathname);

  return (
    <PageLoaderContext.Provider
      value={{
        isLoading,
        message: activeMessage,
        setPageLoading,
      }}
    >
      {children}
    </PageLoaderContext.Provider>
  );
};
